import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCheck, ChevronLeft, ChevronRight, Mail, MailOpen } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { useNotificationStore } from '../stores/notificationStore';
import { checkboxClass } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useConfirm } from '../components/ui/ConfirmDialog';

interface Notif {
  id: string;
  type: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';
  title: string;
  body: string;
  payload: unknown;
  read_at: string | null;
  created_at: string;
}

interface NotifPage {
  content: Notif[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const PAGE_SIZE = 20;

/** Section 28b — Notifications. V1: in-app polling every 60 s. */
export default function Notifications() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const refreshUnread = useNotificationStore((s) => s.refresh);
  const unread = useNotificationStore((s) => s.unread);
  const [rows, setRows] = useState<Notif[]>([]);
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get<NotifPage>(
        `/notifications?unreadOnly=${unreadOnly}&page=${page}&size=${PAGE_SIZE}`
      );
      setRows(data.content ?? []);
      setTotal(data.totalElements ?? 0);
      // Marking rows read (or the 60 s poll) can shrink the list out from under the
      // current page — fall back to the last page that still has rows.
      if ((data.content ?? []).length === 0 && page > 0) {
        setPage(Math.max(0, Math.min(page, data.totalPages ?? 0) - 1));
      }
    } catch (err) {
      showApiError(err);
    }
  };
  useEffect(() => {
    // The unread count comes from the shared store (the appbar bell polls it too) and gates the
    // "Mark all read" button, so refresh it alongside the list rather than letting the button sit
    // enabled against a count the page has already invalidated.
    const tick = () => void Promise.all([load(), refreshUnread()]);
    tick();
    const t = setInterval(tick, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadOnly, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /**
   * Clear the whole unread list. Confirmed rather than immediate, and the dialog spells out the
   * shared-channel effect: `read_at` lives on the notification, not per recipient, and nearly
   * every row is addressed to a ROLE — so one person clearing them clears them for every
   * colleague holding that role. That is the existing model (see NotificationService.markAllRead),
   * but it is the kind of thing a user should be told before, not after.
   */
  const markAllRead = async () => {
    const ok = await confirm({
      title: t('notifications.confirmMarkAll', { count: unread }),
      description: t('notifications.confirmMarkAllDetail'),
      confirmLabel: t('notifications.markAll'),
    });
    if (!ok) return;
    setMarkingAll(true);
    try {
      const { data } = await apiClient.patch<{ data: { marked: number } }>(
        '/notifications/read-all',
        {}
      );
      const n = data.data.marked;
      push({ tone: 'success', message: t('notifications.marked', { count: n }) });
      // Back to the first page: with "unread only" on, everything we just cleared has left the
      // list, so whatever page we were on is now empty.
      setPage(0);
      await Promise.all([load(), refreshUnread()]);
    } catch (err) {
      showApiError(err);
    } finally {
      setMarkingAll(false);
    }
  };

  const markRead = async (id: string) => {
    try {
      await apiClient.patch(
        `/notifications/${id}/read`,
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      // Reload the list AND the appbar badge — the bell reads the same store, so the count
      // drops now instead of waiting out its 60 s poll (or a page refresh).
      await Promise.all([load(), refreshUnread()]);
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3 border-b pb-4">
        <h1 className="text-xl font-semibold tracking-tight">{t('notifications.title')}</h1>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          disabled={unread === 0 || markingAll}
          title={
            unread === 0
              ? t('notifications.nothingUnread')
              : t('notifications.markAllTitle', { count: unread })
          }
          onClick={markAllRead}
        >
          <CheckCheck className="h-3.5 w-3.5" />
          {markingAll
            ? t('notifications.marking')
            : unread > 0
              ? t('notifications.markAllWithCount', { count: unread })
              : t('notifications.markAll')}
        </Button>
        <label className="text-sm flex items-center gap-1">
          <input
            className={checkboxClass}
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => {
              setPage(0);
              setUnreadOnly(e.target.checked);
            }}
          />
          {t('notifications.unreadOnly')}
        </label>
      </header>
      <ul className="space-y-2">
        {rows.map((n) => (
          <li key={n.id} className={`border rounded p-3 bg-card ${n.read_at ? 'opacity-60' : ''}`}>
            <div className="flex items-start gap-2">
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${
                  {
                    CRITICAL: 'bg-destructive/20',
                    HIGH: 'bg-destructive/10',
                    NORMAL: 'bg-muted',
                    LOW: 'bg-muted/30',
                  }[n.priority]
                }`}
              >
                {t(`notifications.priority.${n.priority}`, { defaultValue: n.priority })}
              </span>
              <div className="flex-1">
                <div className="font-semibold text-sm">{n.title}</div>
                <div className="text-sm">{n.body}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {n.type} · {n.created_at}
                </div>
              </div>
              {!n.read_at && (
                <button
                  onClick={() => markRead(n.id)}
                  aria-label={t('notifications.markAsRead')}
                  title={t('notifications.markAsRead')}
                  className="group shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {/* Sealed envelope at rest, opening on hover/focus — the icon shows the
                      state the row is in and the state the click will put it in. */}
                  <Mail size={16} className="group-hover:hidden group-focus-visible:hidden" />
                  <MailOpen
                    size={16}
                    className="hidden group-hover:block group-focus-visible:block"
                  />
                </button>
              )}
            </div>
          </li>
        ))}
        {rows.length === 0 && (
          <li className="text-sm text-muted-foreground">{t('notifications.empty')}</li>
        )}
      </ul>
      {total > 0 && (
        <div className="flex items-center justify-between border-t pt-2 text-xs text-muted-foreground">
          <span>{t('notifications.pager', { page: page + 1, totalPages, count: total })}</span>
          <div className="flex gap-1">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex h-7 items-center rounded-md border border-input bg-background px-2 transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> {t('ui.prev')}
            </button>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="inline-flex h-7 items-center rounded-md border border-input bg-background px-2 transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40"
            >
              {t('ui.next')} <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
